/**
 * IslandBarometer — inline SVG pressure gauge for the footer.
 * Small (32px) with colored arc. Tapping expands to near-fullscreen overlay.
 * Needle driven by macroState. Arc colored cold→hot for instant readability.
 */

import { memo, useEffect, useRef, useState } from 'react';
import WhyBuddyPanel from './WhyBuddyPanel';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useSpring, useTransform } from 'framer-motion';

interface IslandBarometerProps {
  macroState: string;
  moodLabel?: string;
  plainSubtitle?: string;
  whatItMeans?: string;
  expanded: boolean;
  onClose: () => void;
  advisory?: {
    macroLabel: string;
    bubbleTitle: string;
    oneLiner: string;
    bubbleBody: string;
    confidence: string;
  };
  // WhyBuddy panel props
  networkState?: string;
  blockState?: string;
  activeSignature?: string | null;
  heatLevel?: number;
  recentEventHeadline?: string | null;
  ltConfidence?: string;
}

const MACRO_ANGLES: Record<string, number> = {
  DEEP_VALUE: -108,
  ACCUMULATION: -60,
  NEUTRAL: 0,
  WARM: 60,
  HOT: 108,
};

const MACRO_LABEL_TEXT: Record<string, string> = {
  DEEP_VALUE: 'Deep Value',
  ACCUMULATION: 'Accumulation',
  NEUTRAL: 'Neutral',
  WARM: 'Warming Up',
  HOT: 'Overheated',
  MIXED: 'Mixed Signals',
};

const MACRO_LABEL_COLOR: Record<string, string> = {
  DEEP_VALUE: 'hsl(var(--energy) / 0.7)',
  ACCUMULATION: 'hsl(var(--success) / 0.6)',
  NEUTRAL: 'hsl(var(--foreground) / 0.4)',
  WARM: 'hsl(var(--bitcoin) / 0.7)',
  HOT: 'hsl(var(--stress) / 0.7)',
  MIXED: 'hsl(var(--mood) / 0.5)',
};

const CX = 24;
const CY = 24;
const R = 18;
const ARC_START = 220;
const ARC_END = 320;
const ARC_MID = (ARC_START + ARC_END) / 2;

function polarToXY(cx: number, cy: number, r: number, deg: number) {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx: number, cy: number, r: number, startDeg: number, endDeg: number) {
  const s = polarToXY(cx, cy, r, startDeg);
  const e = polarToXY(cx, cy, r, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
}

// 5 ticks evenly across arc
const TICK_ANGLES = [ARC_START, ARC_START + 25, ARC_MID, ARC_END - 25, ARC_END];

// Colored arc segments: cold(blue) → accumulation(teal) → neutral(gray) → warm(orange) → hot(red)
const ARC_SEGMENTS = [
  { start: ARC_START, end: ARC_START + 20, color: '#4a9eda' },      // deep value - blue
  { start: ARC_START + 20, end: ARC_START + 40, color: '#5cb89a' }, // accumulation - teal
  { start: ARC_START + 40, end: ARC_MID + 10, color: '#8a8a7a' },   // neutral - muted
  { start: ARC_MID + 10, end: ARC_END - 20, color: '#d4913a' },     // warm - orange
  { start: ARC_END - 20, end: ARC_END, color: '#c45c3e' },          // hot - red
];

function GaugeSVG({ size, needleRotate, strokeScale = 1 }: { size: number; needleRotate: any; strokeScale?: number }) {
  const needleLen = 16;
  return (
    <svg width={size} height={size * 56 / 48} viewBox="0 0 48 56" fill="none">
      {/* Ground shadow */}
      <ellipse cx="24" cy="53" rx="10" ry="2.5" fill="#0a0500" opacity="0.18" />
      {/* Post */}
      <rect x="22.5" y="40" width="3" height="8" rx="1" fill="#1e1008" />
      {/* Outer ring */}
      <circle cx={CX} cy={CY} r="22" fill="#1e1008" stroke="#8a6040" strokeWidth={2 * strokeScale} />
      {/* Glass */}
      <circle cx={CX} cy={CY} r="20" fill="hsl(30 15% 90% / 0.10)" />

      {/* Colored arc segments */}
      {ARC_SEGMENTS.map((seg, i) => (
        <path
          key={i}
          d={describeArc(CX, CY, R, seg.start, seg.end)}
          stroke={seg.color}
          strokeWidth={2.5 * strokeScale}
          opacity="0.5"
          fill="none"
          strokeLinecap="round"
        />
      ))}

      {/* Tick marks */}
      {TICK_ANGLES.map((deg, i) => {
        const outer = polarToXY(CX, CY, R + 1, deg);
        const inner = polarToXY(CX, CY, R - 2.5, deg);
        return (
          <line
            key={i}
            x1={inner.x} y1={inner.y} x2={outer.x} y2={outer.y}
            stroke="#8a6040" strokeWidth={0.8 * strokeScale} opacity="0.5" strokeLinecap="round"
          />
        );
      })}

      {/* Needle */}
      <motion.g
        style={{
          originX: `${CX}px`,
          originY: `${CY}px`,
          rotate: needleRotate,
        }}
      >
        <line
          x1={CX} y1={CY} x2={CX} y2={CY - needleLen}
          stroke="#f7931a" strokeWidth={1.5 * strokeScale} strokeLinecap="round"
        />
      </motion.g>

      {/* Pivot */}
      <circle cx={CX} cy={CY} r={2 * strokeScale} fill="#f7931a" />
    </svg>
  );
}

function IslandBarometerInner({ macroState, moodLabel, plainSubtitle, whatItMeans, advisory, expanded, onClose, networkState, blockState, activeSignature, heatLevel, recentEventHeadline, ltConfidence }: IslandBarometerProps) {
  const isMixed = macroState === 'MIXED';
  const baseAngle = MACRO_ANGLES[macroState] ?? 0;

  const [wobble, setWobble] = useState(0);
  const rafRef = useRef(0);

  useEffect(() => {
    let running = true;
    const loop = (t: number) => {
      if (!running) return;
      if (isMixed) {
        setWobble(Math.sin((t / 3000) * Math.PI * 2) * 5);
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      running = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, [isMixed]);

  const needleAngle = baseAngle + (isMixed ? wobble : 0);
  const springAngle = useSpring(needleAngle, { stiffness: 100, damping: 16 });

  useEffect(() => {
    springAngle.set(needleAngle);
  }, [needleAngle, springAngle]);

  const needleRotate = useTransform(springAngle, (v) => v + 180);

  useEffect(() => {
    if (!expanded) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [expanded, onClose]);

  const labelText = MACRO_LABEL_TEXT[macroState] ?? 'Observing';
  const labelColor = MACRO_LABEL_COLOR[macroState] ?? 'hsl(var(--foreground) / 0.4)';

  return (
    <>
      {/* Expanded overlay — portalled to body */}
      {createPortal(
        <AnimatePresence>
          {expanded && (
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center pointer-events-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => onClose()}
            >
              <motion.div
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  background: 'hsl(var(--background) / 0.88)',
                  backdropFilter: 'blur(24px)',
                }}
              />

              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.6, opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
                className="relative z-10 flex flex-col items-center px-5"
                style={{ width: 'min(320px, calc(100vw - 32px))' }}
                onClick={() => onClose()}
              >
                {/* Gauge */}
                <div style={{ width: 160, height: 187 }}>
                  <GaugeSVG size={160} needleRotate={needleRotate} strokeScale={0.7} />
                </div>

                {/* Scale labels */}
                <div className="flex items-center justify-between w-[150px] -mt-3 mb-3">
                  <span className="text-[6px] font-body font-medium" style={{ color: '#4a9eda' }}>Cold</span>
                  <span className="text-[6px] font-body font-light" style={{ color: 'hsl(var(--foreground) / 0.15)' }}>·</span>
                  <span className="text-[6px] font-body font-medium" style={{ color: '#8a8a7a' }}>Neutral</span>
                  <span className="text-[6px] font-body font-light" style={{ color: 'hsl(var(--foreground) / 0.15)' }}>·</span>
                  <span className="text-[6px] font-body font-medium" style={{ color: '#c45c3e' }}>Hot</span>
                </div>

                {/* ── Primary: Mood label ── */}
                <motion.p
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15, duration: 0.3 }}
                  className="font-body font-semibold text-[16px] tracking-wide"
                  style={{ color: labelColor }}
                >
                  {moodLabel || labelText}
                </motion.p>

                {/* Macro state tag */}
                {moodLabel && moodLabel !== labelText && (
                  <p
                    className="text-[8px] font-body font-medium uppercase tracking-[0.15em] mt-1"
                    style={{ color: 'hsl(var(--foreground) / 0.2)' }}
                  >
                    {labelText}
                  </p>
                )}

                {/* ── Plain subtitle ── */}
                {plainSubtitle && (
                  <motion.p
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.22, duration: 0.3 }}
                    className="font-body font-medium text-[11.5px] leading-[1.55] mt-2.5 max-w-[260px] mx-auto text-center"
                    style={{ color: 'hsl(var(--foreground) / 0.6)' }}
                  >
                    {plainSubtitle}
                  </motion.p>
                )}

                {/* ── Divider ── */}
                <div
                  className="mx-auto mt-4 mb-3.5"
                  style={{
                    height: 1,
                    width: '50%',
                    background: 'linear-gradient(90deg, transparent, hsl(var(--foreground) / 0.08), transparent)',
                  }}
                />

                {/* ── Advisory block ── */}
                {advisory && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.28, duration: 0.35 }}
                    className="text-center space-y-3"
                  >
                    <p
                      className="text-[11px] font-body font-medium italic leading-relaxed"
                      style={{ color: 'hsl(var(--foreground) / 0.5)' }}
                    >
                      {advisory.oneLiner}
                    </p>

                    <p
                      className="text-[10px] font-body font-light leading-[1.65] max-w-[250px] mx-auto"
                      style={{ color: 'hsl(var(--foreground) / 0.38)' }}
                    >
                      {advisory.bubbleBody}
                    </p>


                    {/* Confidence dots */}
                    <div className="flex items-center justify-center gap-1.5 pt-0.5">
                      {(['LOW', 'MEDIUM', 'HIGH'] as const).map((level) => (
                        <div
                          key={level}
                          className="w-1.5 h-1.5 rounded-full"
                          style={{
                            background: ['LOW', 'MEDIUM', 'HIGH'].indexOf(advisory.confidence) >= ['LOW', 'MEDIUM', 'HIGH'].indexOf(level)
                              ? labelColor
                              : 'hsl(var(--foreground) / 0.06)',
                          }}
                        />
                      ))}
                      <span
                        className="text-[7px] font-body font-light uppercase tracking-wider ml-1"
                        style={{ color: 'hsl(var(--foreground) / 0.2)' }}
                      >
                        {advisory.confidence === 'HIGH' ? 'High confidence' : advisory.confidence === 'MEDIUM' ? 'Medium confidence' : 'Low confidence'}
                      </span>
                    </div>
                  </motion.div>
                )}

                {/* ── Why Buddy looks like this ── */}
                <div className="mt-4">
                  <WhyBuddyPanel
                    macroState={macroState}
                    networkState={networkState}
                    blockState={blockState}
                    activeSignature={activeSignature}
                    heatLevel={heatLevel}
                    recentEventHeadline={recentEventHeadline}
                    ltConfidence={ltConfidence}
                    whatItMeans={whatItMeans}
                  />
                </div>

                {/* ── Footer ── */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.45 }}
                  className="text-[7px] font-body font-light italic mt-5"
                  style={{ color: 'hsl(var(--foreground) / 0.12)' }}
                >
                  Observation only · not financial advice
                </motion.p>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="text-[8px] font-body font-light mt-2.5 cursor-pointer"
                  style={{ color: 'hsl(var(--foreground) / 0.18)' }}
                  onClick={() => onClose()}
                >
                  tap to close
                </motion.p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}

const IslandBarometer = memo(IslandBarometerInner);
export default IslandBarometer;
